import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface AgentStatus {
  agentEnabled: boolean;
  agentEnabledAt: string | null;
  agentPaused: boolean;
  pausedAt: string | null;
  resumedAt: string | null;
  lastReconciledAt: string | null;
  autoCreateSubscriptions: boolean;
  hubspotWriteBackEnabled: boolean;
}

export interface Prerequisites {
  hubspotConnected: boolean;
  stripeConnected: boolean;
  settingsExist: boolean;
  canEnable: boolean;
}

export interface RevSyncSettings {
  // Read-only status fields
  agentEnabled: boolean;
  agentEnabledAt: string | null;
  agentPaused: boolean;
  lastReconciledAt: string | null;
  // Toggle 1
  autoCreateSubscriptions: boolean;
  netNewDealPipelineId: string | null;
  netNewDealStageId: string | null;
  // Toggle 2
  enableRenewalDeals: boolean;
  renewalPipelineId: string | null;
  renewalStageId: string | null;
  // Note: Contracts are automatically created when subscriptions are created
  closedWonPipelineId: string | null;
  closedWonStageIds: string[];
  // Toggle 4 (Enterprise only)
  syncContractsToHubSpot: boolean;
  // Toggle 5
  cSuiteDailyReportEnabled: boolean;
  // Enterprise check
  hubspotEnterpriseCapable: boolean;
  // Legacy (hide from UI)
  hubspotWriteBackEnabled: boolean;
}

export interface PipelineStage {
  id: string;
  label: string;
  closedWon: boolean;
  probability: number;
}

export interface Pipeline {
  id: string;
  label: string;
  stages: PipelineStage[];
}

export interface AISuggestion {
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

export interface RenewalSuggestion {
  sourcePipeline: {
    pipelineId: string;
    pipelineLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
  destinationPipeline: {
    pipelineId: string;
    pipelineLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
  destinationStage: {
    pipelineId: string;
    pipelineLabel: string;
    stageId: string;
    stageLabel: string;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  } | null;
}

export interface SubscriptionSetupSuggestion {
  useProductLibrary?: boolean;
  defaultProductId?: string;
  defaultProductName?: string;
  fieldMappings?: {
    amount?: { field: string; confidence: "high" | "medium" | "low" };
    billingCycle?: { field: string; confidence: "high" | "medium" | "low" };
    startDate?: { field: string; confidence: "high" | "medium" | "low" };
    currency?: { field: string; confidence: "high" | "medium" | "low" };
  };
}

export interface SettingsSuggestions {
  netNewDeal: AISuggestion | null;
  // contractCreation removed - contracts are automatic with subscriptions
  renewalDeal: RenewalSuggestion;
  subscriptionSetup?: SubscriptionSetupSuggestion;
  provider: "openai" | "gemini" | "anthropic" | "fallback";
}

export interface SettingsWithSuggestions {
  settings: RevSyncSettings;
  pipelines: Pipeline[];
  suggestions: SettingsSuggestions;
  isFirstTimeSetup: boolean;
  userMessage?: string;
}

export interface UpdateSettingsRequest {
  autoCreateSubscriptions?: boolean;
  netNewDealPipelineId?: string | null;
  netNewDealStageId?: string | null;
  enableRenewalDeals?: boolean;
  renewalPipelineId?: string | null;
  renewalStageId?: string | null;
  // autoCreateContractOnClosedWon removed - contracts are automatic with subscriptions
  syncContractsToHubSpot?: boolean;
  cSuiteDailyReportEnabled?: boolean;
}

export interface Reconciliation {
  id: string;
  mode: "auto" | "incremental" | "backfill";
  subscriptionsScanned: number;
  contractsUpserted: number;
  errors: string[];
  startedAt: string;
  completedAt: string | null;
  status: "running" | "completed" | "failed";
}

export interface ReconciliationHistory {
  reconciliations: Reconciliation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface TriggerReconciliationResponse {
  success: boolean;
  mode: "auto" | "incremental" | "backfill";
  message: string;
  userMessage?: string;
  startedAt: string;
}

export interface Contract {
  id: string;
  customerName: string | null;
  customerEmail: string | null;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  hubspotDealId: string | null;
  status: "active" | "pending" | "expired" | "cancelled";
  mrr: number;
  arr: number;
  startDate: string;
  endDate: string | null;
  isHistorical: boolean;
  source:
    | "webhook"
    | "stripe_reconcile"
    | "stripe_backfill"
    | "automated"
    | null;
  createdAt: string;
  updatedAt: string;
  urls?: ContractUrls;
}

export interface ContractUrls {
  hubspot?: {
    deal?: string;
    contact?: string;
    company?: string;
  };
  stripe?: {
    subscription?: string;
    customer?: string;
  };
}

export interface ContractDetail extends Contract {
  hubspotPortalId: string | null;
  hubspotCompanyId: string | null;
  hubspotContactId: string | null;
  linkConfidence: "high" | "medium" | "low" | "none";
  requiresReview: boolean;
  urls?: ContractUrls;
}

export interface ContractPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ContractListResponse {
  contracts: Contract[];
  pagination: ContractPagination;
}

export interface ContractFilters {
  page?: number;
  limit?: number;
  status?: "active" | "pending" | "expired" | "cancelled";
  isHistorical?: boolean;
  hubspotLinked?: boolean;
  sortBy?:
    | "createdAt"
    | "updatedAt"
    | "startDate"
    | "endDate"
    | "mrr"
    | "arr"
    | "status";
  sortOrder?: "asc" | "desc";
}

export class RevSyncService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/revsync";
  }

  async getAgentStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: AgentStatus; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<AgentStatus>>(
        `${this.baseUrl}/agent/status`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch agent status.";
      throw new Error(errorMessage);
    }
  }

  async checkPrerequisites(): Promise<Prerequisites> {
    try {
      const [hubspotStatus, stripeStatus, settings] = await Promise.allSettled([
        httpService.get<
          ApiResponse<{ connected: boolean; hubspotInstanceId?: string }>
        >("/hubspot/status"),
        httpService.get<ApiResponse<{ connected: boolean }>>("/stripe/status"),
        httpService
          .get<ApiResponse<any>>(`${this.baseUrl}/settings`)
          .catch(() => null),
      ]);

      const hubspotData =
        hubspotStatus.status === "fulfilled" ? hubspotStatus.value.data : null;
      const stripeData =
        stripeStatus.status === "fulfilled" ? stripeStatus.value.data : null;
      const settingsData =
        settings.status === "fulfilled" && settings.value
          ? settings.value.data
          : null;

      const hubspotConnected =
        hubspotData?.connected === true && !!hubspotData?.hubspotInstanceId;
      const stripeConnected = stripeData?.connected === true;
      const settingsExist = !!settingsData;

      return {
        hubspotConnected,
        stripeConnected,
        settingsExist,
        canEnable: hubspotConnected && stripeConnected,
      };
    } catch (error: any) {
      console.error("Error checking prerequisites:", error);
      return {
        hubspotConnected: false,
        stripeConnected: false,
        settingsExist: false,
        canEnable: false,
      };
    }
  }

  async enableAgent(config?: AxiosRequestConfig): Promise<{
    data: { success: boolean; agentEnabled: boolean; agentEnabledAt: string };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentEnabled: boolean;
          agentEnabledAt: string;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/enable`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to enable agent.";
      throw new Error(errorMessage);
    }
  }

  async pauseAgent(config?: AxiosRequestConfig): Promise<{
    data: { success: boolean; agentPaused: boolean; pausedAt: string };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentPaused: boolean;
          pausedAt: string;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/pause`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to pause agent.";
      throw new Error(errorMessage);
    }
  }

  async resumeAgent(config?: AxiosRequestConfig): Promise<{
    data: {
      success: boolean;
      agentPaused: boolean;
      resumedAt: string;
      reconciliationTriggered: boolean;
      userMessage?: string;
    };
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          success: boolean;
          agentPaused: boolean;
          resumedAt: string;
          reconciliationTriggered: boolean;
          userMessage?: string;
        }>
      >(`${this.baseUrl}/agent/resume`, {}, config);
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to resume agent.";
      throw new Error(errorMessage);
    }
  }

  async getSettings(
    config?: AxiosRequestConfig
  ): Promise<{ data: RevSyncSettings; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<RevSyncSettings>>(
        `${this.baseUrl}/settings`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch settings.";
      throw new Error(errorMessage);
    }
  }

  async updateSettings(
    settings: UpdateSettingsRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: RevSyncSettings; message: string; userMessage?: string; productLibraryAutoEnabled?: boolean; productCount?: number; defaultProductName?: string }> {
    try {
      if (settings.autoCreateSubscriptions) {
        if (!settings.netNewDealPipelineId || !settings.netNewDealStageId) {
          throw new Error(
            "Pipeline and Stage are required when auto-create is enabled"
          );
        }
      }

      if (settings.enableRenewalDeals) {
        if (!settings.renewalPipelineId || !settings.renewalStageId) {
          throw new Error(
            "Renewal Pipeline and Stage are required when renewals are enabled"
          );
        }
      }

      const cleanedSettings = Object.entries(settings).reduce(
        (acc, [key, value]) => {
          acc[key] = value === "" ? null : value;
          return acc;
        },
        {} as Record<string, any>
      );

      const response = await httpService.put<ApiResponse<RevSyncSettings & { userMessage?: string; productLibraryAutoEnabled?: boolean; productCount?: number; defaultProductName?: string }>>(
        `${this.baseUrl}/settings`,
        cleanedSettings,
        config
      );
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data.userMessage,
        productLibraryAutoEnabled: response.data.productLibraryAutoEnabled,
        productCount: response.data.productCount,
        defaultProductName: response.data.defaultProductName,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update settings.";
      throw new Error(errorMessage);
    }
  }

  async getSettingsWithSuggestions(
    forceSuggestions: boolean = false,
    config?: AxiosRequestConfig
  ): Promise<{ data: SettingsWithSuggestions; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<SettingsWithSuggestions>
      >(`${this.baseUrl}/settings/with-suggestions`, {
        ...config,
        params: { forceSuggestions },
      });
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch settings with suggestions.";
      throw new Error(errorMessage);
    }
  }

  async getReconciliationHistory(
    page: number = 1,
    limit: number = 50,
    config?: AxiosRequestConfig
  ): Promise<{ data: ReconciliationHistory; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<ReconciliationHistory>
      >(`${this.baseUrl}/reconcile/history`, {
        ...config,
        params: { page, limit },
      });
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch reconciliation history.";
      throw new Error(errorMessage);
    }
  }

  async triggerReconciliation(
    mode?: "auto" | "incremental" | "backfill",
    config?: AxiosRequestConfig
  ): Promise<{
    data: TriggerReconciliationResponse;
    message: string;
    userMessage?: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<TriggerReconciliationResponse>
      >(
        `${this.baseUrl}/reconcile`,
        mode ? { mode } : {},
        config
      );
      return {
        data: response.data,
        message: response.message,
        userMessage: response.data?.userMessage,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to trigger reconciliation.";
      throw new Error(errorMessage);
    }
  }

  async listContracts(
    filters: ContractFilters = {},
    config?: AxiosRequestConfig
  ): Promise<{ data: ContractListResponse; message: string }> {
    try {
      const params: Record<string, string | number> = {
        page: filters.page || 1,
        limit: filters.limit || 50,
      };

      if (filters.status) {
        params.status = filters.status;
      }

      if (filters.isHistorical !== undefined) {
        params.isHistorical = String(filters.isHistorical);
      }

      if (filters.hubspotLinked !== undefined) {
        params.hubspotLinked = String(filters.hubspotLinked);
      }

      if (filters.sortBy) {
        params.sortBy = filters.sortBy;
      }

      if (filters.sortOrder) {
        params.sortOrder = filters.sortOrder;
      }

      const response = await httpService.get<ApiResponse<ContractListResponse>>(
        `${this.baseUrl}/contracts`,
        {
          ...config,
          params,
        }
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch contracts.";
      throw new Error(errorMessage);
    }
  }

  async getContract(
    contractId: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: ContractDetail; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<{ contract: ContractDetail }>>(
        `${this.baseUrl}/contracts/${contractId}`,
        config
      );
      return {
        data: response.data.contract,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch contract.";
      throw new Error(errorMessage);
    }
  }

  async clearData(config?: AxiosRequestConfig): Promise<{
    data: {
      deleted: {
        contracts: number;
        globalCustomers: number;
        reconciliationRuns: number;
        fieldMappings: number;
        hubSpotObjectMappings: number;
        renewalReminders: number;
        hubSpotSyncLogs: number;
      };
      settingsReset: boolean;
      agentUninstalled: boolean;
    };
    message: string;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<{
          deleted: {
            contracts: number;
            globalCustomers: number;
            reconciliationRuns: number;
            fieldMappings: number;
            hubSpotObjectMappings: number;
            renewalReminders: number;
            hubSpotSyncLogs: number;
          };
          settingsReset: boolean;
          agentUninstalled: boolean;
        }>
      >(`${this.baseUrl}/clear-data`, {}, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error || "Failed to clear data.";
      throw new Error(errorMessage);
    }
  }
}

export const revSyncService = new RevSyncService();
